import { createManyEntities } from '@/common/reducers/backend-api/backend-api';
import { dispatch } from '@/extension/context/common/store/extension-store';
import moment from 'moment';
import { shouldPersistSessionEvent, signatureOf } from '@/common/session-events/session-event-dedupe';

export interface SessionEvent {
    _id?: string;
    owner?: string;
    sessionId?: string;
    state?: string;
    stream?: string;
    hasSynced?: boolean;
    qrcode?: string;
    triggerdBy?: string;
    status?: string;
    fromDateTime?: string;
    untilDateTime?: string;
    podId?: string;
    version?: number;
    event?: string;
    details?: any;
}

export class SessionEventService {
    private static instance: SessionEventService;
    private sessionEventBuffer: SessionEvent[] = [];
    private readonly REPORT_INTERVAL_MS = 1 * 60 * 60 * 1000; // 1 hours
    private readonly STORAGE_KEY = 'bt_session_events';
    private readonly BUFFERING_WILL_START_AFTER_X_EVENTS = 5;
    private readonly BUFFER_SIZE = 10;
    private eventIndex = 0;
    /** Signature of the last observation actually persisted. Lives OUTSIDE the buffer on
     *  purpose: the buffer is emptied by flushSessionEventBuffer() and the content script
     *  remounts constantly on WhatsApp Web, so neither survives long enough to dedupe on. */
    private lastSignature: string | null = null;
    private reportTimeout: NodeJS.Timeout | null = null;

    // invariant: assigned via setSessionContext() called from owning lifecycle before use
    private ownerId!: string;
    private sessionId!: string;
    private localSessionId!: string;
    private podId!: string;

    private restartNextReportSchedule(): void {
        if (this.reportTimeout) {
            clearTimeout(this.reportTimeout);
        }

        this.reportTimeout = setTimeout(() => {
            if (this.sessionEventBuffer.length > 0) {
                console.log('[waStateManager] [SessionEventService] (ReportSchedule) flushing buffer');
                this.flushSessionEventBuffer();
            }
            this.restartNextReportSchedule();
        }, this.REPORT_INTERVAL_MS);
    }

    private loadFromLocalStorage(): void {
        try {
            const storedData = localStorage.getItem(this.STORAGE_KEY);
            if (storedData) {
                const parsedData = JSON.parse(storedData);
                this.sessionEventBuffer = parsedData.sessionEventBuffer || [];
                this.lastSignature = parsedData.lastSignature ?? null;
                this.eventIndex = parsedData.eventIndex ?? 0;
            }
        } catch (error) {
            console.error('[waStateManager] [SessionEventService] (loadFromLocalStorage) Failed to load session events from local storage:', error);
        }
    }

    private saveToLocalStorage(): void {
        try {
            const dataToStore = {
                sessionEventBuffer: this.sessionEventBuffer,
                // Persisted so dedupe survives a flush AND a remount — see session-event-dedupe.ts.
                lastSignature: this.lastSignature,
                // Persisted too: eventIndex gates the immediate-flush path, and resetting it on
                // every remount is what turned "flush the first 5 fast" into "flush everything".
                eventIndex: this.eventIndex
            };
            localStorage.setItem(this.STORAGE_KEY, JSON.stringify(dataToStore));
        } catch (error) {
            console.error('[waStateManager] [SessionEventService] (saveToLocalStorage) Failed to save session events to local storage:', error);
        }
    }

    private constructor() {
        this.loadFromLocalStorage();
        this.restartNextReportSchedule();
    }

    public setSessionContext(ownerId: string, sessionId: string, localSessionId: string, podId: string) {
        this.ownerId = ownerId;
        this.sessionId = sessionId;
        this.localSessionId = localSessionId;
        this.podId = podId;
    }
    public makeSureLocalStorageValuesAreSet() {
        if (!this.sessionId) {
            this.sessionId = window.localStorage.getItem("sessionId") || '';
        }
        if (!this.podId) {
            this.podId = window.localStorage.getItem("podId") || '';
        }
    }

    public static getInstance(): SessionEventService {
        if (!SessionEventService.instance) {
            SessionEventService.instance = new SessionEventService();
        }
        return SessionEventService.instance;
    }

    private async sendSessionEvents(events: SessionEvent[]): Promise<void> {
        if (events.length === 0) return;

        try {
            dispatch(
                createManyEntities.initiate({
                    service: 'session-events',
                    entities: events,
                })
            ).unwrap();

            this.restartNextReportSchedule(); // Reset the timer after successful send

        } catch (error) {
            console.error('[waStateManager] [SessionEventService] (sendSessionEvents) Failed to send buffer:', error);
        }
    }

    public updateSessionEventBuffer(newEvent: SessionEvent): void {
        console.log('[waStateManager] [SessionEventService] (updateSessionEventBuffer) newEvent:', newEvent);

        this.makeSureLocalStorageValuesAreSet();

        const now = moment().utc().toString();

        const lastIndex = this.sessionEventBuffer.length - 1;
        if (lastIndex >= 0) {
            this.sessionEventBuffer[lastIndex].untilDateTime = now;
        }

        // Dedupe against the last PERSISTED signature, not against the buffer's tail. The old
        // check read `this.sessionEventBuffer[lastIndex]`, and flushSessionEventBuffer() sets the
        // buffer to [] — so after every flush `lastIndex < 0` and the next poll was written
        // unconditionally. Measured 2026-08-04 that produced ~504,000 docs/day of unchanged
        // state against ~2,000/day of real lifecycle events.
        if (shouldPersistSessionEvent(this.lastSignature, newEvent as any)) {
            const newSessionEvent = {
                ...newEvent,
                fromDateTime: now,
                untilDateTime: now,
                // `owner`, NOT `ownerId`: the Mongo schema field is `owner`
                // (models/session-events.model.ts), and mongoose strict mode silently DROPS
                // undeclared keys — which is why 2.98M of 3.00M docs had no owner at all and
                // none of this history could be attributed to a customer.
                owner: this.ownerId,
                sessionId: this.sessionId || this.localSessionId,
                sessionType: this.sessionId ? 'remote' : 'local',
                triggerdBy: newEvent.triggerdBy,
                podId: this.podId
            };
            this.sessionEventBuffer.push(newSessionEvent);
            this.lastSignature = signatureOf(newEvent as any);
        }


        if (this.eventIndex < this.BUFFERING_WILL_START_AFTER_X_EVENTS) {
            console.log('[waStateManager] [SessionEventService] (updateSessionEventBuffer) eventIndex:', this.eventIndex);
            this.eventIndex++;
            this.flushSessionEventBuffer();
        }
        else if (
            this.sessionEventBuffer.length >= this.BUFFER_SIZE
        ) {
            console.log('[waStateManager] [SessionEventService] (updateSessionEventBuffer) flushing buffer');
            this.flushSessionEventBuffer();
        }

        this.saveToLocalStorage();
    }

    private flushSessionEventBuffer(): void {
        this.sendSessionEvents([...this.sessionEventBuffer]);
        this.sessionEventBuffer = [];
    }
}
